/**
 * One toggleable row inside MultiTagFilterDropdown — a colored tag, or the
 * muted "no tag" row. For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

import { getTagOptionStyle } from '../TagFilterDropdown';

export interface MultiTagOptionProps {
  tag: string;
  /** Shown instead of the raw tag value (used by the "no tag" row). */
  label?: string;
  isSelected: boolean;
  onToggle: () => void;
  tagColors?: Record<string, string>;
  /** Muted styling with no color swatch — for the "no tag" row. */
  muted?: boolean;
}

const Box: FC<{ isSelected: boolean; bordered: boolean }> = ({ isSelected, bordered }) => (
  <span
    className={cn(
      'relative flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
      isSelected && bordered ? 'border-current bg-current/20' : '',
      isSelected && !bordered ? 'border-current' : '',
      !isSelected && 'border-input',
    )}
  >
    {isSelected && <Check className="h-3 w-3 stroke-[3]" />}
  </span>
);

export const MultiTagOption: FC<MultiTagOptionProps> = ({
  tag,
  label,
  isSelected,
  onToggle,
  tagColors,
  muted = false,
}) => {
  const tagStyle = muted ? undefined : getTagOptionStyle(tag, tagColors, isSelected);
  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      className={cn(
        'group relative flex w-full items-center gap-2 overflow-hidden px-3 py-2 text-left text-sm font-medium transition-colors',
        muted && 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        muted && isSelected && 'bg-accent text-accent-foreground',
        !muted && !tagStyle && 'hover:bg-accent/60',
        !muted && !tagStyle && isSelected && 'bg-accent/70',
        !muted && isSelected && tagStyle && 'ring-1 ring-inset ring-black/15 dark:ring-white/25',
      )}
      style={tagStyle}
      onClick={onToggle}
    >
      {tagStyle && (
        <span
          className="pointer-events-none absolute inset-0 bg-white/20 opacity-0 transition-opacity duration-150 group-hover:opacity-100 dark:bg-black/20"
          aria-hidden
        />
      )}
      <Box isSelected={isSelected} bordered={!muted} />
      <span className="relative min-w-0 truncate">{label ?? tag}</span>
    </button>
  );
};
